import { render, showApiError, showToast } from '../main';
import {
  TOAST_COMMENT_REJECTED,
  API_ERR_SAVE_ANSWER, API_ERR_SAVE_COMMENT, API_ERR_DELETE_ANSWER,
} from '../utils/app-strings';
import { qaApi } from '../api';
import { navigate, getCurrentPath, extractParams } from '../router';
import { debounce, showSaveIndicator, loadingSpinner, errorState } from '../utils';
import { saveIndicator } from '../utils/html';
import {
  QaQuestionItem, AnswerEntry,
  escapeBasic, renderControl, attachQuestionHandlers, collectAnswerData,
  renderSaveButton, renderCommentInput, collectCommentText,
} from './qa-shared-renderers';

// ============================================================================
// Types
// ============================================================================

interface QaSetDetail {
  id: string;
  title: string;
  description: string | null;
}

// ============================================================================
// State
// ============================================================================

let setDetail: QaSetDetail | null = null;
let questions: QaQuestionItem[] = [];
let answerMap = new Map<string, AnswerEntry>();

// ============================================================================
// Entry Point
// ============================================================================

export async function renderQuestionSet(): Promise<void> {
  const path = getCurrentPath();
  const params = extractParams('/questions/:id', path);
  const setId = params.id;

  if (!setId) {
    navigate('/questions');
    return;
  }

  render(`
    <div class="view-frame">
      <div class="page-header">
        <button class="back-btn" id="qs-back-btn">
          <i class="fas fa-arrow-left"></i>
        </button>
        <span class="page-title">Loading...</span>
      </div>
      <div class="view-scroll" id="qs-content">
        ${loadingSpinner()}
      </div>
    </div>
  `);

  document.getElementById('qs-back-btn')?.addEventListener('click', () => navigate('/questions'));

  try {
    const [setRes, questionsRes, answersRes] = await Promise.all([
      qaApi.getSet(setId),
      qaApi.getSetQuestions(setId),
      qaApi.getMyAnswersForSet(setId),
    ]);

    setDetail = setRes.data;
    questions = questionsRes.data;

    // Build answer map keyed by question_id
    answerMap = new Map();
    for (const a of answersRes.data) {
      answerMap.set(a.question_id, {
        answerId: a.id,
        version: a.version || null,
        comment_text: a.comment_text ?? null,
      });
    }

    renderPage();
  } catch (err: any) {
    const content = document.getElementById('qs-content');
    if (content) {
      content.innerHTML = errorState({ title: 'Failed to load questions', retryId: 'qs-retry-btn' });
      document.getElementById('qs-retry-btn')?.addEventListener('click', renderQuestionSet);
    }
  }
}

// ============================================================================
// Page Rendering
// ============================================================================

function renderPage(): void {
  // Update header title
  const title = document.querySelector('.page-title');
  if (title && setDetail) title.textContent = setDetail.title;

  const content = document.getElementById('qs-content');
  if (!content) return;

  const answeredCount = answerMap.size;
  const totalCount = questions.length;

  content.innerHTML = `
    ${setDetail?.description ? `<div class="qs-description">${escapeBasic(setDetail.description)}</div>` : ''}
    <div class="qs-progress" id="qs-progress">${answeredCount} / ${totalCount} answered</div>
    <div class="qs-questions">
      ${questions.map((q, i) => renderQuestionCard(q, i + 1)).join('')}
    </div>
  `;

  // Attach event handlers
  for (const q of questions) {
    if (q.answer_type === 'free_text') {
      attachQuestionHandlers(q, triggerSave, { skipFreeTextAutoSave: true });
      attachFreeTextSaveBtn(q);
    } else if (q.answer_type === 'multi_choice') {
      attachQuestionHandlers(q, triggerSave, { skipMultiChoiceAutoSave: true });
      attachFreeTextSaveBtn(q);
      attachCommentSaveBtn(q);
    } else if (q.answer_type === 'numeric') {
      attachQuestionHandlers(q, triggerSave, { skipNumericAutoSave: true });
      attachFreeTextSaveBtn(q);
      attachCommentSaveBtn(q);
    } else {
      attachQuestionHandlers(q, triggerSave);
      attachCommentSaveBtn(q);
    }
  }
}

function updateProgress(): void {
  const el = document.getElementById('qs-progress');
  if (el) {
    el.textContent = `${answerMap.size} / ${questions.length} answered`;
  }
}

// ============================================================================
// Question Card Rendering
// ============================================================================

function renderQuestionCard(q: QaQuestionItem, num: number): string {
  const entry = answerMap.get(q.id);
  const isNonFreeText = q.answer_type !== 'free_text';

  return `
    <div class="card card-bordered qs-card" id="q-card-${q.id}">
      <div class="qs-card-header">
        <span class="qs-card-num">${num}.</span>
        <span class="qs-card-text">${escapeBasic(q.question_text)}</span>
        ${saveIndicator(`indicator-q-${q.id}`)}
      </div>
      ${q.help_text ? `<div class="qs-card-help">${escapeBasic(q.help_text)}</div>` : ''}
      <div class="qs-card-control" id="q-control-${q.id}">
        ${renderControl(q, entry)}
        ${q.answer_type === 'free_text' || q.answer_type === 'multi_choice' || q.answer_type === 'numeric' ? renderSaveButton(q.id) : ''}
        ${isNonFreeText ? renderCommentInput(q.id, entry?.comment_text) : ''}
      </div>
    </div>
  `;
}

// ============================================================================
// Save Button for Free-Text
// ============================================================================

function attachFreeTextSaveBtn(q: QaQuestionItem): void {
  const saveBtn = document.getElementById(`qa-save-${q.id}`);
  if (!saveBtn) return;
  saveBtn.addEventListener('click', () => executeImmediateSave(q));
}

function attachCommentSaveBtn(q: QaQuestionItem): void {
  const saveBtn = document.getElementById(`q-comment-save-${q.id}`);
  if (!saveBtn) return;
  saveBtn.addEventListener('click', async () => {
    const existing = answerMap.get(q.id);
    if (!existing) return; // Can't save comment without an answer

    const comment = collectCommentText(q.id);
    const data = collectAnswerData(q);
    if (!data) return;
    if (comment !== undefined) data.comment_text = comment;

    const btn = saveBtn as HTMLButtonElement;
    btn.disabled = true;
    btn.textContent = 'Saving...';

    try {
      const res = await qaApi.editAnswer(existing.answerId, data);
      answerMap.set(q.id, {
        answerId: res.data.answer.id,
        version: res.data.version,
        comment_text: res.data.comment_rejected ? null : (comment || null),
      });
      if (res.data.comment_rejected) showToast(TOAST_COMMENT_REJECTED, 'error');
      showSaveIndicator(`indicator-q-${q.id}`, true);
    } catch (err: any) {
      showSaveIndicator(`indicator-q-${q.id}`, false, 'Error');
      showApiError(err, API_ERR_SAVE_COMMENT);
    } finally {
      btn.disabled = false;
      btn.textContent = 'Save';
    }
  });
}

async function executeImmediateSave(q: QaQuestionItem): Promise<void> {
  const data = collectAnswerData(q);

  // Empty free_text with an existing answer → soft-delete the answer
  const existing = answerMap.get(q.id);
  if (!data && q.answer_type === 'free_text' && existing) {
    const saveBtn = document.getElementById(`qa-save-${q.id}`) as HTMLButtonElement | null;
    if (saveBtn) { saveBtn.disabled = true; saveBtn.textContent = 'Saving...'; }
    try {
      await qaApi.deleteAnswer(existing.answerId);
      answerMap.delete(q.id);
      updateProgress();
      showSaveIndicator(`indicator-q-${q.id}`, true);
    } catch (err: any) {
      showSaveIndicator(`indicator-q-${q.id}`, false, 'Error');
      showApiError(err, API_ERR_DELETE_ANSWER);
    } finally {
      if (saveBtn) { saveBtn.disabled = false; saveBtn.textContent = 'Save'; }
    }
    return;
  }

  if (!data) return;

  const saveBtn = document.getElementById(`qa-save-${q.id}`) as HTMLButtonElement | null;
  if (saveBtn) {
    saveBtn.disabled = true;
    saveBtn.textContent = 'Saving...';
  }

  try {
    let commentRejected = false;
    if (existing) {
      const res = await qaApi.editAnswer(existing.answerId, data);
      commentRejected = !!res.data.comment_rejected;
      answerMap.set(q.id, {
        answerId: res.data.answer.id,
        version: res.data.version,
      });
    } else {
      const res = await qaApi.submitAnswer({ question_id: q.id, ...data });
      commentRejected = !!res.data.comment_rejected;
      answerMap.set(q.id, {
        answerId: res.data.answer.id,
        version: res.data.version,
      });
      updateProgress();
    }
    if (commentRejected) showToast(TOAST_COMMENT_REJECTED, 'error');
    showSaveIndicator(`indicator-q-${q.id}`, true);
    if (saveBtn) {
      saveBtn.disabled = false;
      saveBtn.textContent = 'Save';
    }
  } catch (err: any) {
    showSaveIndicator(`indicator-q-${q.id}`, false, 'Error');
    showApiError(err, API_ERR_SAVE_ANSWER);
    if (saveBtn) {
      saveBtn.disabled = false;
      saveBtn.textContent = 'Save';
    }
  }
}

// ============================================================================
// Auto-save
// ============================================================================

function triggerSave(q: QaQuestionItem): void {
  debounce(`qa-${q.id}`, async () => {
    const data = collectAnswerData(q);
    if (!data) return;

    // Include comment_text for non-free-text questions
    const comment = collectCommentText(q.id);
    if (comment !== undefined) data.comment_text = comment;

    try {
      const existing = answerMap.get(q.id);
      let commentRejected = false;
      if (existing) {
        const res = await qaApi.editAnswer(existing.answerId, data);
        commentRejected = !!res.data.comment_rejected;
        answerMap.set(q.id, {
          answerId: res.data.answer.id,
          version: res.data.version,
          comment_text: commentRejected ? null : (comment || null),
        });
      } else {
        const res = await qaApi.submitAnswer({ question_id: q.id, ...data });
        commentRejected = !!res.data.comment_rejected;
        answerMap.set(q.id, {
          answerId: res.data.answer.id,
          version: res.data.version,
          comment_text: commentRejected ? null : (comment || null),
        });
        updateProgress();
      }
      if (commentRejected) showToast(TOAST_COMMENT_REJECTED, 'error');
      showSaveIndicator(`indicator-q-${q.id}`, true);
    } catch (err: any) {
      showSaveIndicator(`indicator-q-${q.id}`, false, 'Error');
      showApiError(err, API_ERR_SAVE_ANSWER);
    }
  });
}
